import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  INTERCEPTInternalServerError,
  INTERCEPTNotFound,
  INTERCEPTUnauthorized,
} from 'app/core/interceptors/contexts';
import { ServiceBase } from 'app/core/services/service.base';
import { CampaignCarouselSlide } from 'app/shared/models/campaign';

const CAROUSEL_ENDPOINT = 'campaign-carousel';

/**
 * Source of the campaigns rendered in the dashboard carousel.
 *
 * @remarks
 * Stateless by design. The carousel is identical for every user and carries no
 * "already seen" bookkeeping — nothing is cached, nothing is persisted, and the
 * slides are fetched fresh whenever the dashboard renders. The image URLs are
 * short-lived SAS links, so caching them across sessions would hand the carousel
 * expired signatures anyway.
 */
@Injectable({ providedIn: 'root' })
export class CampaignCarouselService extends ServiceBase {
  private readonly http = inject(HttpClient);

  /**
   * Fetches the campaigns to render in the carousel.
   *
   * @returns The slides, newest first, or an empty array when there is nothing
   * to show.
   *
   * @remarks
   * Takes no language: nothing in a slide is localized server-side, so the same
   * response serves every language and a language switch needs no refetch.
   *
   * Every failure degrades to an empty array. The carousel is supplementary
   * content on a page the user came to for something else, so a failed request
   * hides it instead of surfacing a toast or a maintenance redirect — which is
   * also why the global interceptor is muted for this call.
   */
  fetchCampaigns(): Observable<CampaignCarouselSlide[]> {
    return this.http
      .get<CampaignCarouselSlide[]>(
        `${this.UrlServiceV1}${CAROUSEL_ENDPOINT}`,
        {
          ...this.obterAuthHeader(),
          context: this.buildSilentContext(),
        },
      )
      .pipe(
        map((slides) => {
          if (!Array.isArray(slides)) {
            return [];
          }

          return slides;
        }),
        catchError(() => of([])),
      );
  }

  /** Keeps the global interceptor out of a request the user never asked for. */
  private buildSilentContext(): HttpContext {
    return new HttpContext()
      .set(INTERCEPTInternalServerError, false)
      .set(INTERCEPTUnauthorized, false)
      .set(INTERCEPTNotFound, false);
  }
}
